// Stats (DEVELOPMENT.md: Screens): per person, viewable by the other: puzzles solved, by type,
// score spread, average time per difficulty band. No comparisons or rankings. No mockup: it uses
// the Settings cards, the accent colour for counts and the score scale for score bands.
import type { StatsResponse } from '@zinc/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { Page } from '../components/AppShell';
import { formatTime } from '../components/solve/SolveHeader';
import { Card, ScreenHeader, SectionTitle } from '../components/ui';
import { api } from '../lib/api';
import { typeName } from '../lib/library';
import { useMe } from '../lib/queries';
import { chipColours, scoreColour } from '../lib/score';

function Number_({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-1 flex-col gap-0.5 px-3.5 py-3">
      <span className="text-[26px] leading-none font-bold tabular-nums">{value}</span>
      <span className="text-[13px] text-muted">{label}</span>
    </div>
  );
}

export function StatsPage() {
  const { who = 'me' } = useParams();
  const me = useMe();
  const otherName = me.data?.other?.displayName ?? null;
  const stats = useQuery({
    queryKey: ['stats', who],
    queryFn: () => api<StatsResponse>(`/api/stats/${who}`),
  });
  const s = stats.data;
  const title =
    who === 'other' ? `${s?.user.displayName ?? otherName ?? ''}’s stats` : 'Your stats';
  const maxType = Math.max(1, ...(s?.byType ?? []).map((t) => t.count));
  const spread = (s?.scoreSpread ?? []).filter((b) => b.band !== 'unrated');
  const unrated = s?.scoreSpread.find((b) => b.band === 'unrated')?.count ?? 0;
  const maxSpread = Math.max(1, ...spread.map((b) => b.count));

  return (
    <>
      <ScreenHeader title={title} back={true} />
      <Page>
        {stats.isError ? (
          <Card className="px-4 py-5 text-[15px]">{stats.error.message}</Card>
        ) : !s ? null : (
          <>
            <Card className="flex divide-x divide-line-soft">
              <Number_ value={s.solved} label="Solved" />
              <Number_ value={s.solvedThisWeek} label="This week" />
              <Number_ value={s.inProgress} label="In progress" />
            </Card>

            <section className="flex flex-col gap-2">
              <SectionTitle>By type</SectionTitle>
              <Card className="flex flex-col gap-2 px-3.5 py-3">
                {s.byType.length === 0 ? (
                  <span className="text-[14px] text-muted">Nothing solved yet.</span>
                ) : (
                  s.byType.map((t) => (
                    <div key={t.type} className="flex items-center gap-3 text-[14px]">
                      <span className="w-[42%] shrink-0 truncate">{typeName(t.type)}</span>
                      <span className="h-2 flex-1 overflow-hidden rounded-full bg-segment">
                        <span
                          className="block h-full rounded-full bg-accent"
                          style={{ width: `${(t.count / maxType) * 100}%` }}
                        />
                      </span>
                      <span className="w-8 text-right font-semibold tabular-nums">{t.count}</span>
                    </div>
                  ))
                )}
              </Card>
            </section>

            <section className="flex flex-col gap-2">
              <SectionTitle>Score spread</SectionTitle>
              <Card className="flex flex-col gap-2 px-3.5 py-3">
                <div
                  className="flex h-28 items-end gap-1.5"
                  role="img"
                  aria-label={spread.map((b) => `${b.band}: ${b.count}`).join(', ')}
                >
                  {spread.map((b, i) => (
                    <div key={b.band} className="flex h-full flex-1 flex-col justify-end gap-1">
                      <span className="text-center text-[11px] text-muted tabular-nums">
                        {b.count || ''}
                      </span>
                      <span
                        className="block rounded-t-[4px]"
                        style={{
                          height: `${(b.count / maxSpread) * 80}%`,
                          minHeight: b.count ? 3 : 0,
                          background: scoreColour(i + 0.5),
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[11px] text-muted tabular-nums">
                  <span>0</span>
                  <span>5</span>
                  <span>10</span>
                </div>
                {unrated > 0 && (
                  <span className="text-[13px] text-muted">
                    And {unrated} unrated {unrated === 1 ? 'puzzle' : 'puzzles'}.
                  </span>
                )}
              </Card>
            </section>

            <section className="flex flex-col gap-2">
              <SectionTitle>Average time by difficulty</SectionTitle>
              <Card className="flex flex-col overflow-hidden">
                {s.timeByBand.map((b, i) => {
                  const [lo] = b.band.split('-').map(Number) as [number];
                  return (
                    <div
                      key={b.band}
                      className={`flex min-h-12 items-center gap-3 px-3.5 text-[15px] ${i < s.timeByBand.length - 1 ? 'border-b border-solid border-line-soft' : ''}`}
                    >
                      <span
                        className="inline-flex h-[22px] w-14 shrink-0 items-center justify-center rounded-full text-[12px] font-bold tabular-nums"
                        style={chipColours(lo + 1)}
                      >
                        {b.band.replace('-', '–')}
                      </span>
                      <span className="flex-1 text-[13px] text-muted">
                        {b.count} {b.count === 1 ? 'puzzle' : 'puzzles'}
                      </span>
                      <span className="font-semibold tabular-nums">
                        {b.averageMs === null ? '–' : formatTime(b.averageMs)}
                      </span>
                    </div>
                  );
                })}
              </Card>
              {s.timesLimited && (
                <p className="mx-1 my-0 text-[12px] text-muted">
                  Times count only puzzles you’ve solved too.
                </p>
              )}
            </section>

            {otherName && (
              <Link
                to={who === 'other' ? '/stats/me' : '/stats/other'}
                className="mx-1 text-[14px] font-semibold"
              >
                {who === 'other' ? 'Your stats' : `${otherName}’s stats`}
              </Link>
            )}
          </>
        )}
      </Page>
    </>
  );
}
